import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "../i18n";
import useSeo from "../hooks/useSeo";
import { api } from "../api";
import {
  CheckCircle2, MessageCircle, FileText, Download, BookOpen,
  Play, Pause, ChevronLeft, ChevronRight,
  DownloadCloud, FolderOpen, MousePointer2, Settings2, Bell,
} from "lucide-react";

const DOCS_FR = [
  {
    key: "install",
    file: "/assets/Guide_Installation_DrTradeScalperPro_v1.24.pdf",
    filename: "Guide_Installation_DrTradeScalperPro_v1.24.pdf",
    size: "818 Ko",
    icon: FileText,
    hue: "emerald",
  },
  {
    key: "manual",
    file: "/assets/Manuel_Utilisation_DrTradeScalperPro_v1.24_FR.pdf",
    filename: "Manuel_Utilisation_DrTradeScalperPro_v1.24_FR.pdf",
    size: "1.3 Mo",
    icon: BookOpen,
    hue: "cyan",
  },
];

const DOCS_EN = [
  {
    key: "install",
    file: "/assets/Installation_Guide_DrTradeScalperPro_v1.24_EN.pdf",
    filename: "Installation_Guide_DrTradeScalperPro_v1.24_EN.pdf",
    size: "816 KB",
    icon: FileText,
    hue: "emerald",
  },
  {
    key: "manual",
    file: "/assets/User_Manual_DrTradeScalperPro_v1.24_EN.pdf",
    filename: "User_Manual_DrTradeScalperPro_v1.24_EN.pdf",
    size: "835 KB",
    icon: BookOpen,
    hue: "cyan",
  },
];

const DOCS_ES = [
  {
    key: "install",
    file: "/assets/Installation_Guide_DrTradeScalperPro_v1.24_ES.pdf",
    filename: "Installation_Guide_DrTradeScalperPro_v1.24_ES.pdf",
    size: "818 KB",
    icon: FileText,
    hue: "emerald",
  },
  {
    key: "manual",
    file: "/assets/User_Manual_DrTradeScalperPro_v1.24_ES.pdf",
    filename: "User_Manual_DrTradeScalperPro_v1.24_ES.pdf",
    size: "836 KB",
    icon: BookOpen,
    hue: "cyan",
  },
];

const DOCS_DE = [
  {
    key: "install",
    file: "/assets/Installation_Guide_DrTradeScalperPro_v1.24_DE.pdf",
    filename: "Installation_Guide_DrTradeScalperPro_v1.24_DE.pdf",
    size: "818 KB",
    icon: FileText,
    hue: "emerald",
  },
  {
    key: "manual",
    file: "/assets/User_Manual_DrTradeScalperPro_v1.24_DE.pdf",
    filename: "User_Manual_DrTradeScalperPro_v1.24_DE.pdf",
    size: "836 KB",
    icon: BookOpen,
    hue: "cyan",
  },
];

const hueClasses = {
  emerald: {
    icon: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
    btn: "bg-emerald-400 hover:bg-emerald-300 text-[#05050A]",
  },
  cyan: {
    icon: "text-cyan-300 bg-cyan-400/10 border-cyan-400/30",
    btn: "bg-cyan-400 hover:bg-cyan-300 text-[#05050A]",
  },
  amber: {
    icon: "text-amber-300 bg-amber-400/10 border-amber-400/30",
    btn: "bg-amber-400 hover:bg-amber-300 text-[#05050A]",
  },
};

const STEP_ICONS = [DownloadCloud, FolderOpen, MousePointer2, Settings2, Bell];
const STEP_DURATION_MS = 5000;

function AnimatedSteps({ steps }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());
  const n = steps.length;

  useEffect(() => {
    if (paused || n === 0) return undefined;
    startRef.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / STEP_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        setIdx((i) => (i + 1) % n);
        startRef.current = Date.now();
        setProgress(0);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, idx, n]);

  const goTo = (i) => {
    setIdx(((i % n) + n) % n);
    setProgress(0);
    startRef.current = Date.now();
  };

  if (n === 0) return null;
  const ActiveIcon = STEP_ICONS[idx % STEP_ICONS.length] || CheckCircle2;
  const active = steps[idx];

  return (
    <div className="relative glass rounded-2xl p-6 sm:p-8 overflow-hidden" data-testid="tutorials-steps-animated">
      {/* Background glow following active step */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at ${(idx / Math.max(1, n - 1)) * 100}% 0%, rgba(34,211,238,0.18), transparent 60%)`,
        }}
      />

      {/* Top row: step counter + controls */}
      <div className="relative flex items-center justify-between mb-6">
        <div className="text-xs font-mono text-zinc-500 tracking-wider">
          STEP {String(idx + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => goTo(idx - 1)}
            className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 grid place-items-center transition"
            data-testid="steps-prev-btn"
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="h-8 w-8 rounded-lg bg-cyan-400/15 border border-cyan-400/30 hover:bg-cyan-400/25 grid place-items-center text-cyan-300 transition"
            data-testid="steps-pause-btn"
            aria-label={paused ? "Play" : "Pause"}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => goTo(idx + 1)}
            className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 grid place-items-center transition"
            data-testid="steps-next-btn"
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Active step content with fade-in/slide-in */}
      <div className="relative flex gap-4 sm:gap-5 items-start min-h-[128px]" key={idx}>
        <div
          className="shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-cyan-400/15 border border-cyan-400/40 grid place-items-center text-cyan-300"
          style={{ animation: "stepIconPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }}
        >
          <ActiveIcon className="h-6 w-6 sm:h-7 sm:w-7" />
        </div>
        <div
          className="flex-1 min-w-0"
          style={{ animation: "stepTextIn 0.55s ease-out both" }}
        >
          <div
            className="text-base sm:text-lg font-semibold leading-snug mb-2"
            data-testid={`tutorial-step-${idx}-title`}
          >
            {active.t}
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed">{active.d}</div>
        </div>
      </div>

      {/* Dots navigator with progress fill on active */}
      <div className="relative mt-7 flex items-center gap-2">
        {steps.map((_, i) => {
          const isActive = i === idx;
          const isDone = i < idx;
          return (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              className="group flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden relative"
              data-testid={`steps-dot-${i}`}
              aria-label={`Go to step ${i + 1}`}
            >
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-cyan-400"
                style={{
                  width: isActive ? `${progress}%` : isDone ? "100%" : "0%",
                  transition: isActive ? "none" : "width 0.4s ease",
                }}
              />
            </button>
          );
        })}
      </div>

      {/* CSS keyframes (scoped via style tag) */}
      <style>{`
        @keyframes stepIconPop {
          0% { transform: scale(0.6) rotate(-10deg); opacity: 0; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes stepTextIn {
          0% { transform: translateY(8px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default function Tutorials() {
  const { t, lang } = useI18n();
  const rawSteps = t("tutorials.steps") || [];
  const [eaVersion, setEaVersion] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get("/ea/version").then(({ data }) => {
      if (!cancelled && data?.version) setEaVersion(String(data.version));
    }).catch(() => { /* silencieux : le step reste sans version */ });
    return () => { cancelled = true; };
  }, []);
  // Injecte la version courante de l'EA dans le libellé du 1er step
  const steps = rawSteps.map((s, i) => {
    if (i !== 0 || !eaVersion) return s;
    const marker = "(v" + eaVersion + ")";
    return { ...s, t: s.t.includes(marker) ? s.t : `${s.t} ${marker}` };
  });
  // Mapping langue → docs. Si une langue n'a pas de PDF dédié (encore non
  // généré), on retombe sur la version EN.
  const DOCS_BY_LANG = { fr: DOCS_FR, en: DOCS_EN, es: DOCS_ES, de: DOCS_DE };
  const DOCS = DOCS_BY_LANG[lang] || DOCS_EN;

  useSeo({
    title: lang === "fr"
      ? "Tutoriels & Téléchargements PDF — Installation Bot MT5 Dr Trade Scalper Pro"
      : "Tutorials & PDF Downloads — Dr Trade Scalper Pro MT5 Bot Install Guide",
    description: lang === "fr"
      ? "Guides officiels PDF (v1.24) : installation pas-à-pas et manuel utilisateur. 5 étapes pour lancer votre bot de trading MT5 en 5 minutes."
      : "Official PDF guides (v1.24): step-by-step installation and user manual. 5 steps to launch your MT5 trading bot in 5 minutes.",
    keywords: lang === "fr"
      ? "installation EA MT5, tutoriel MetaTrader 5, guide robot trading, PDF Expert Advisor, configurer bot MT5"
      : "MT5 EA install, MetaTrader 5 tutorial, trading robot guide, Expert Advisor PDF, configure MT5 bot",
    path: "/tutorials",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="page-tutorials">
      <div className="mb-12">
        <div className="section-overline mb-3">{t("tutorials.overline")}</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter">{t("tutorials.title")}</h1>
      </div>

      {/* Downloads section (in-hero) */}
      <section className="mb-14" data-testid="tutorials-downloads">
        <h2 className="text-xl sm:text-2xl font-semibold mb-2">
          {t("tutorials.downloads_title")}
        </h2>
        <p className="text-sm text-zinc-400 mb-6">{t("tutorials.downloads_sub")}</p>

        <div className="flex flex-col gap-3">
          {DOCS.map((d) => {
            const Icon = d.icon;
            const hue = hueClasses[d.hue];
            return (
              <a
                key={d.key}
                href={d.file}
                download={d.filename}
                className="glass rounded-2xl p-4 flex items-center gap-4 hover:border-white/20 transition-all group"
                data-testid={`tutorials-download-${d.key}`}
              >
                <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center border ${hue.icon}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-semibold leading-tight">
                    {t(`tutorials.download_${d.key}`)}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 leading-snug line-clamp-2">
                    {t(`tutorials.download_${d.key}_desc`)}
                  </div>
                </div>
                <div className="hidden sm:block text-[11px] text-zinc-500 font-mono shrink-0">
                  PDF · {d.size}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors shrink-0 ${hue.btn}`}>
                  <Download className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t("tutorials.download_cta")}</span>
                </span>
              </a>
            );
          })}
        </div>
      </section>

      {/* Steps section — animated auto-play stepper */}
      <section data-testid="tutorials-steps">
        <h2 className="text-xl sm:text-2xl font-semibold mb-6">
          {t("tutorials.steps_title")}
        </h2>
        <AnimatedSteps steps={steps} />
      </section>

      <div className="mt-10 text-center text-sm text-zinc-400 inline-flex items-center gap-2 justify-center w-full">
        <MessageCircle className="h-4 w-4 text-cyan-300" />
        {t("tutorials.need_help")}
      </div>
    </div>
  );
}
